import React, { useEffect, useState } from 'react';

import DropDown from '../../common/DropDown';
import history from '../../../history';
import { matchingModeCodes } from '../../../consts';
import { executeHttpRequest } from '../../../epics/api';

type TBaseDropDownProps = {
    renderImage?: any;
    selected: string | number;
    options: any;
    defaultSelection: any;
    filterKey: string;
    defaultSelectionFromParam?: boolean;
    onFilterChange: (...args: unknown[]) => void;
    searchable?: boolean;
    searchPlaceholder?: string;
};

export function BaseDropDown({
    renderImage,
    selected,
    options,
    defaultSelection,
    filterKey,
    defaultSelectionFromParam = true,
    onFilterChange,
    searchable = false,
    searchPlaceholder,
}: TBaseDropDownProps) {
    const renderSelected = (value: { value: any; label: string }) => {
        if (renderImage) {
            return (
                <div className="dropdown-placeholder">
                    {renderImage(value.value)}
                    {value.label}
                </div>
            );
        }

        return <div className="dropdown-placeholder">{value.label}</div>;
    };

    const renderOption = (option: { value: any; label: string }) => {
        if (renderImage) {
            return (
                <div>
                    {renderImage(option.value)}
                    <span>{option.label}</span>
                </div>
            );
        }

        return <div>{option.label}</div>;
    };

    const getDefaultSelection = () => {
        if (selected) {
            const selectedOption = options.find(
                (option: { value: string }) => option.value === selected,
            );

            if (selectedOption) {
                return selectedOption;
            }
        }

        return defaultSelection;
    };

    return (
        <DropDown
            filterKey={filterKey}
            defaultSelectionFromParam={defaultSelectionFromParam}
            selected={selected}
            options={options}
            defaultSelection={getDefaultSelection()}
            renderSelected={renderSelected}
            renderOption={renderOption}
            onFilterChange={onFilterChange}
            searchable={searchable}
            searchPlaceholder={searchPlaceholder}
        />
    );
}

type TOption = {
    value: number | string;
    label: string;
}
type TApiDropDownProps = {
    onFilterChange: () => void;
    currentUrlParams: URLSearchParams;
    url: string;
    filterKey: string;
    labelKey: string;
    valueKey: string;
    verboseNamePlural: string;
    preloadedOptions?: TOption[]
};

export function ApiDropDown({
    onFilterChange,
    currentUrlParams,
    url,
    filterKey,
    labelKey,
    valueKey,
    verboseNamePlural,
    preloadedOptions = null,
}: TApiDropDownProps) {

    const [options, setOptions] = useState([]);
    const defaultOption = {
        label: `All ${verboseNamePlural}`,
        value: '',
    };
    const selectedOption = Number(currentUrlParams.get(filterKey));

    const handleSetOptions = (opts: TOption[]) => {
        const optionsList = [...opts];
        optionsList.unshift(defaultOption);
        setOptions(optionsList);
    };

    useEffect(() => {
        if (!preloadedOptions) {
            executeHttpRequest(`${url}`)
            .then((response => {
                const { content } = response.data;
                handleSetOptions(content.map((option: any) => ({
                    label: option[labelKey],
                    value: option[valueKey],
                })));
            }))
            .catch(err => {
                // eslint-disable-next-line no-console
                console.log(err);
                handleSetOptions([]);
            });
        } else {
            handleSetOptions(preloadedOptions);
        }
    }, [preloadedOptions]);

    return (
        <>
            {options && (
                <BaseDropDown
                    filterKey={filterKey}
                    selected={selectedOption}
                    options={options}
                    defaultSelection={defaultOption}
                    onFilterChange={onFilterChange}
                />
            )}
        </>
    );
}


export function ServiceDropDown({
    onFilterChange,
}: {
    onFilterChange: () => void;
}) {
    const defaultSelection = {
        label: 'All Services',
        value: '',
    };

    const options = [
        {
            label: 'Hospitality',
            value: 'hospitality',
        },
        {
            label: 'Delivery',
            value: 'driver',
        },
        {
            label: 'Industrial',
            value: 'industry',
        },
        {
            label: 'All Services',
            value: '',
        },
    ];

    const currentUrlParams = new URLSearchParams(history.location.search);

    return (
        <BaseDropDown
            filterKey="service"
            selected={currentUrlParams.get('service')}
            options={options}
            defaultSelection={defaultSelection}
            onFilterChange={onFilterChange}
        />
    );
}

export function MatchingDropDown({
    onFilterChange,
}: {
    onFilterChange: () => void;
}) {
    const defaultSelection = {
        label: 'All bookings',
        value: '',
    };

    const options = [
        {
            label: 'All bookings',
            value: '',
        },
        {
            label: 'Manually matched',
            value: matchingModeCodes.MANUAL,
        },
        {
            label: 'Semi-auto matched',
            value: matchingModeCodes.SEMI_AUTO,
        },
        {
            label: 'Auto-matched',
            value: matchingModeCodes.AUTO,
        },
    ];

    const currentUrlParams = new URLSearchParams(history.location.search);

    return (
        <BaseDropDown
            filterKey="matching_mode"
            selected={currentUrlParams.get('matching_mode')}
            options={options}
            defaultSelection={defaultSelection}
            onFilterChange={onFilterChange}
        />
    );
}

export function InterviewDropDown({
    onFilterChange,
}: {
    onFilterChange: () => void;
}) {
    const defaultSelection = {
        label: 'Interview',
        value: '',
    };

    const options = [
        {
            label: 'Passed Interview',
            value: 'OK',
        },
        {
            label: 'Not Passed Interview',
            value: 'KO',
        },
        {
            label: 'Scheduled',
            value: 'SC',
        },
        {
            label: 'No show',
            value: 'NS',
        },
        {
            label: 'All interview statuses',
            value: '',
        },
    ];

    const currentUrlParams = new URLSearchParams(history.location.search);

    return (
        <BaseDropDown
            filterKey="passed_interview"
            selected={currentUrlParams.get('passed_interview')}
            options={options}
            defaultSelection={defaultSelection}
            onFilterChange={onFilterChange}
        />
    );
}

export function PublishedDropDown({
    onFilterChange,
}: {
    onFilterChange: () => void;
}) {
    const defaultSelection = {
        label: 'All statuses',
        value: '',
    };

    const options = [
        {
            label: 'Published',
            value: 'published',
        },
        {
            label: 'Unpublished',
            value: 'unpublished',
        },
        {
            label: 'Inactive',
            value: 'inactive',
        },
        {
            label: 'All statuses',
            value: '',
        },
    ];

    const currentUrlParams = new URLSearchParams(history.location.search);

    return (
        <BaseDropDown
            filterKey="worker_status"
            selected={currentUrlParams.get('worker_status')}
            options={options}
            defaultSelection={defaultSelection}
            onFilterChange={onFilterChange}
        />
    );
}

export function BookingSheetStatusDropdown({
    onFilterChange,
}: {
    onFilterChange: () => void;
}) {
    const defaultSelection = {
        label: 'All bookings',
        value: '',
    };

    const options = [
        {
            label: 'All bookings',
            value: '',
        },
        {
            label: 'All valid bookings',
            value: 'AV',
        },
        {
            label: 'Clock-in & clock-out issues',
            value: 'CI',
        },
        {
            label: 'Bookings on standby',
            value: 'BU',
        },
        {
            label: 'Backup cancelled',
            value: 'BC',
        },
        {
            label: 'Employer cancelled',
            value: 'CA',
        },
        {
            label: 'Worker cancelled',
            value: 'FC',
        },
        {
            label: 'No shows',
            value: 'NS',
        },
        {
            label: 'Unsatisfied',
            value: 'UN',
        },
        {
            label: 'Feedbacks required',
            value: 'FR',
        },
        {
            label: 'Missing feedbacks',
            value: 'MF',
        },
    ];

    const currentUrlParams = new URLSearchParams(history.location.search);

    return (
        <BaseDropDown
            filterKey="bookings_status"
            selected={currentUrlParams.get('bookings_status')}
            options={options}
            defaultSelection={defaultSelection}
            onFilterChange={onFilterChange}
        />
    );
}

export function BookingSheetClientStatusDropdown({
    onFilterChange,
}: {
    onFilterChange: () => void;
}) {
    const defaultSelection = {
        label: 'All bookings',
        value: '',
    };

    const options = [
        {
            label: 'All bookings',
            value: '',
        },
        {
            label: 'All valid bookings',
            value: 'AV',
        },
        {
            label: 'Clock-out issues',
            value: 'PE',
        },
        {
            label: 'Feedbacks required',
            value: 'FR',
        },
        {
            label: 'Missing feedbacks',
            value: 'MF',
        },
    ];

    const currentUrlParams = new URLSearchParams(history.location.search);

    return (
        <BaseDropDown
            filterKey="bookings_status"
            selected={currentUrlParams.get('bookings_status')}
            options={options}
            defaultSelection={defaultSelection}
            onFilterChange={onFilterChange}
        />
    );
}

export function AmountStatusDropdown({
    onFilterChange,
}: {
    onFilterChange: () => void;
}) {
    const defaultSelection = {
        label: 'All Amounts',
        value: '',
    };

    const options = [
        {
            label: 'All Amounts',
            value: '',
        },
        {
            label: 'All Settled',
            value: 'AS',
        },
        {
            label: 'Refunds',
            value: 'RF',
        },
        {
            label: 'To Pay',
            value: 'TP',
        },
        {
            label: 'To Pay > £100.00',
            value: 'TP100',
        },
    ];

    const currentUrlParams = new URLSearchParams(history.location.search);

    return (
        <BaseDropDown
            filterKey="amount_status"
            selected={currentUrlParams.get('amount_status')}
            options={options}
            defaultSelection={defaultSelection}
            onFilterChange={onFilterChange}
        />
    );
}
